import { useCallback } from 'react';
import Input from '../components/Input';
import useUXStore from '../api/ux/store';
import { DialogInfo } from '../api/ux/data';
import Logo from "../assets/images/logo.png";

interface UsePasswordConfirmationOptions {
    title?: string;
    message?: string;
    onCancel?: () => void;
}

const usePasswordConfirmation = () => {
    const { refreshDialog, hideDialog } = useUXStore();

    const confirm = useCallback((
        onConfirm: (password: string) => Promise<void>,
        options?: UsePasswordConfirmationOptions
    ) => {
        const dialogId = `password-confirm-${Date.now()}`;
        let password = '';
        let showPassword = false;
        let error: string | null = null;
        let isLoading = false;

        const setPassword = (value: string) => {
            password = value;
            updateDialog();
        };

        const setShowPassword = (value: boolean) => {
            showPassword = value;
            updateDialog();
        };

        const setError = (value: string | null) => {
            error = value;
            updateDialog();
        };

        const setIsLoading = (value: boolean) => {
            isLoading = value;
            updateDialog();
        };

        const handleClose = () => {
            hideDialog(dialogId);
            if (options?.onCancel) {
                options.onCancel();
            }
        };

        const handleConfirm = async () => {
            if (!password) {
                setError('Password is required');
                return;
            }

            setError(null);
            setIsLoading(true);

            try {
                await onConfirm(password);
                handleClose();
            } catch (err: any) {
                setError(err.message || 'Invalid password');
                setIsLoading(false);
            }
        };

        const updateDialog = () => {
            const dialogContent = (
                <div style={{ marginBottom: 25, marginTop: 25 }}>
                    {/* Security Header */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginBottom: 20 
                    }}>
                        <img src={Logo} alt="Logo" style={{ width: 100, height: 100 }} />
                    </div>

                    {/* Message */}
                    <p style={{ 
                        marginBottom: 20, 
                        textAlign: 'center',
                        color: '#374151'
                    }}>{options?.message || 'Please enter your password to confirm this action.'}</p>

                    {/* Password Input Container */}
                    <div style={{
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 10
                    }}>
                        <div style={{ position: 'relative' }}>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setPassword(e.target.value);
                                    setError(null);
                                }}
                                placeholder="Password"
                                disabled={isLoading}
                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter' && !isLoading) {
                                        handleConfirm();
                                    }
                                }}
                                style={{
                                    paddingLeft: 40,
                                    borderColor: error ? '#ef4444' : '#e5e7eb'
                                }}
                            />
                            <span style={{
                                position: 'absolute',
                                left: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: 16,
                                color: '#9ca3af'
                            }}>🔒</span>
                        </div>
                        
                        <div style={{ marginTop: 10 }}>
                            <label style={{ 
                                cursor: 'pointer',
                                fontSize: 14,
                                color: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}>
                                <input
                                    type="checkbox"
                                    checked={showPassword}
                                    onChange={(e) => setShowPassword(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                Show password
                            </label>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div style={{ 
                            backgroundColor: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: 6,
                            padding: '8px 12px',
                            marginTop: 10
                        }}>
                            <p style={{ color: '#dc2626', fontSize: 14, margin: 0 }}>{error}</p>
                        </div>
                    )}

                    {/* Security Notice */}
                    <div style={{
                        marginTop: 20,
                        fontSize: 12,
                        color: '#6b7280',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                    }}>
                        <span style={{ fontSize: 10 }}>🔒</span>
                        <span>Your password is encrypted and secure</span>
                    </div>
                </div>
            );

            const dialogInfo: DialogInfo = {
                id: dialogId,
                title: options?.title || 'Password Confirmation Required',
                content: dialogContent,
                visible: true,
                onClose: handleClose,
                onConfirm: () => { handleConfirm();},
                confirmContent: isLoading ? "Verifying..." : "Confirm",
                confirmActive: !isLoading && password.length > 0
            };

            refreshDialog(dialogInfo);
        };

        // Show the dialog initially
        updateDialog();
    }, [refreshDialog, hideDialog]);

    return { confirm };
};

export default usePasswordConfirmation;